import {
  ButtonHTMLAttributes,
  forwardRef,
  type PointerEventHandler,
} from "react";
import { cn } from "@/lib/utils";
import Link from "next/link";
import { lightTap, mediumTap } from "@/lib/haptics";

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: "primary" | "secondary" | "outline" | "ghost";
  size?: "sm" | "md" | "lg";
  isLoading?: boolean;
  href?: string;
  haptic?: PressableHaptic;
  className?: string;
}

type PressableHaptic = "none" | "light" | "medium";

const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      className,
      children,
      variant = "primary",
      size = "md",
      isLoading = false,
      href,
      haptic,
      onPointerDown,
      ...props
    },
    ref,
  ) => {
    const baseStyles =
      "rounded-xl inline-flex items-center justify-center font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 disabled:opacity-50 disabled:pointer-events-none";

    const variants = {
      primary:
        "bg-[var(--button-primary-bg)] text-[var(--button-primary-text)] shadow-[var(--button-primary-shadow)] hover:bg-[var(--button-primary-hover)] focus-visible:ring-[var(--button-primary-focus)]",
      secondary:
        "bg-[var(--button-secondary-bg)] text-[var(--button-secondary-text)] hover:bg-[var(--button-secondary-hover)] focus-visible:ring-stone-400",
      tertiary:
        "bg-foreground text-background hover:bg-foreground/90 focus-visible:ring-primary",
      outline:
        "border border-[var(--color-border-strong)] text-[var(--color-foreground)] bg-[var(--color-surface)] hover:bg-[var(--color-secondary)] focus-visible:ring-stone-400",
      ghost:
        "text-[var(--color-text-light)] hover:bg-[var(--color-secondary)] hover:text-[var(--color-foreground)] focus-visible:ring-stone-400",
    };

    const sizes = {
      sm: "text-xs px-3 py-1.5",
      md: "text-sm px-4 py-2",
      lg: "text-base px-5 py-2.5",
    };

    const buttonClasses = cn(
      baseStyles,
      variants[variant],
      sizes[size],
      isLoading && "opacity-70 cursor-not-allowed",
      className,
    );

    const resolvedHaptic: PressableHaptic = href
      ? "none"
      : (haptic ?? (variant === "primary" ? "light" : "none"));

    if (href) {
      return (
        <Link href={href} className={buttonClasses}>
          {isLoading ? (
            <>
              <svg
                className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                ></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Loading...</span>
            </>
          ) : (
            children
          )}
        </Link>
      );
    }

    const handlePointerDown: PointerEventHandler<HTMLButtonElement> = (e) => {
      if (e.defaultPrevented || isLoading || props.disabled) {
        onPointerDown?.(e);
        return;
      }
      if (e.pointerType === "mouse" && e.button !== 0) {
        onPointerDown?.(e);
        return;
      }
      if (resolvedHaptic === "light") {
        lightTap();
      } else if (resolvedHaptic === "medium") {
        mediumTap();
      }
      onPointerDown?.(e);
    };

    return (
      <button
        className={buttonClasses}
        ref={ref}
        disabled={isLoading}
        {...props}
        onPointerDown={handlePointerDown}
      >
        {isLoading ? (
          <>
            <svg
              className="animate-spin -ml-1 mr-2 h-4 w-4 text-current"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              ></circle>
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              ></path>
            </svg>
            <span>Loading...</span>
          </>
        ) : (
          children
        )}
      </button>
    );
  },
);

Button.displayName = "Button";

export default Button;
